import { useEffect, useMemo, useRef, useState } from 'react';

// gloss
export const useDebounce = <T>(value: T, delay: number = 300): T => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

// gloss
// biome-ignore lint/suspicious/noExplicitAny: generic callback constraint — any[] required to match any function signature
export type DebouncedCallback<T extends (...args: any[]) => unknown> = ((...args: Parameters<T>) => void) & {
  cancel: () => void;
};

// gloss
// biome-ignore lint/suspicious/noExplicitAny: generic callback constraint — any[] required to match any function signature
export const useDebouncedCallback = <T extends (...args: any[]) => unknown>(
  callback: T,
  delay: number = 300,
): DebouncedCallback<T> => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  const delayRef = useRef(delay);
  delayRef.current = delay;

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  return useMemo(() => {
    const debounced = (...args: Parameters<T>) => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => callbackRef.current(...args), delayRef.current);
    };
    debounced.cancel = () => clearTimeout(timerRef.current);
    return debounced;
  }, []);
};
